import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { When } from "react-if";
import {
  ActionStatus,
  AssistantSuggestionPriority,
  IAdhocActionResponse,
  IAdhocActionResponseNew,
  IAssistantEvent,
  IAssistantSuggestionItem,
  OrganisationFeatureFlags,
  PermissionLevel,
} from "shared";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { Toggle } from "../../../../common/Atoms/Toggle";
import { LoadingElementPlaceholder } from "../../../../common/Atoms/LoadingElementPlaceholder";
import { classNames } from "../../../../common/lib/classNames";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { useUser } from "../../../hooks/useUser";
import { useAdhocActionModal } from "../../../hooks/useAdhocActionModal";
import {
  useApplyAssistantSuggestionMutation,
  useCompleteAssistantSuggestionMutation,
  useDismissAssistantSuggestionMutation,
  useGenerateAssistantSuggestionsMutation,
  useGetAssistantSuggestionsQuery,
} from "../../../services/api/assistantApi/assistant";
import { open as openAssistant } from "../../../slices/assistantDrawer";
import { AssistantMarkdown } from "../../Assistant/AssistantMarkdown";
import { toSteps } from "../../Assistant/AssistantMessage";
import { BuilderConfirmModal } from "../../Settings/General/Statement/Builder/BuilderConfirmModal";
import { RaiseEscalationModal } from "../../Escalations/RaiseEscalationModal";
import { RECORD_ASSIGNEE } from "../../Common/Assignee";

const priorityStyle: Record<AssistantSuggestionPriority, { label: string; text: string; pill: string }> = {
  high: { label: `High`, text: `text-red-700`, pill: `text-red-700 bg-red-50` },
  medium: { label: `Medium`, text: `text-orange-700`, pill: `text-orange-700 bg-orange-50` },
  low: { label: `Low`, text: `text-gray-600`, pill: `text-gray-600 bg-gray-100` },
};

/** What clicking the main button will do, shown next to it */
const actionOutcome: Record<NonNullable<IAssistantSuggestionItem[`actionType`]>, string> = {
  send_invoice_reminders: `Sends a reminder email for each invoice now.`,
  send_statements: `Sends a statement to each customer now.`,
  send_sms: `Sends this SMS to each customer's mobile now.`,
  raise_escalations: `Raises an escalation for each customer, assigned to their account owner.`,
};

const BUTTON = `inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md text-[13px] font-semibold whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`;
const PRIMARY_BUTTON = `${BUTTON} bg-paidniceGreen-600 text-white hover:bg-paidniceGreen-700`;
const SECONDARY_BUTTON = `${BUTTON} bg-gray-100 text-gray-700 font-medium hover:bg-gray-200`;

const actionLabel: Record<NonNullable<IAssistantSuggestionItem[`actionType`]>, string> = {
  send_invoice_reminders: `Send reminders`,
  send_statements: `Send statements`,
  send_sms: `Send SMS`,
  raise_escalations: `Raise escalations`,
};

function getErrorMessage(error: unknown, fallbackMessage: string) {
  const data = (error as { error?: { data?: unknown }; data?: unknown } | null)?.error?.data ?? (error as { data?: unknown } | null)?.data;

  if (typeof data === `string` && data.trim()) {
    return data;
  }

  const message = (data as { message?: string; error?: string } | null)?.message ?? (data as { error?: string } | null)?.error;

  return message || fallbackMessage;
}

function formatCents(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: `currency`, currency }).format(cents / 100);
  }
  catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

function targetCount(suggestion: IAssistantSuggestionItem): number {
  return suggestion.actionType === `send_invoice_reminders` ? suggestion.invoiceIds.length : suggestion.contactIds.length;
}

/** One line under the title in the queue: the money at stake when it is one currency, otherwise a count */
function impactLine(suggestion: IAssistantSuggestionItem): string {
  if (suggestion.proposal) {
    return `${suggestion.proposal.ops.length} policy change${suggestion.proposal.ops.length === 1 ? `` : `s`}`;
  }

  if (!suggestion.actionType) {
    return `Advice`;
  }

  const targets = suggestion.targets ?? [];
  const currencies = new Set(targets.map(target => target.currency));

  if (targets.length && currencies.size === 1 && targets.every(target => target.amountCents !== null)) {
    return formatCents(targets.reduce((sum, target) => sum + (target.amountCents ?? 0), 0), targets[0].currency as string);
  }

  const count = targetCount(suggestion);
  const noun = suggestion.actionType === `send_invoice_reminders` ? `invoice` : `customer`;

  return `${count} ${noun}${count === 1 ? `` : `s`}`;
}

/** What clicking "Do it" runs: the ad hoc action with its target count, or the policy change */
function doItLabel(suggestion: IAssistantSuggestionItem): string | null {
  if (suggestion.actionType) {
    const count = suggestion.actionType === `send_invoice_reminders` ? suggestion.invoiceIds.length : suggestion.contactIds.length;

    return `${actionLabel[suggestion.actionType]} (${count})`;
  }

  if (suggestion.proposal) {
    return `Apply change`;
  }

  return null;
}

function targetName(suggestion: IAssistantSuggestionItem, id: string | number): string {
  return suggestion.targets?.find(target => target.id === String(id))?.name ?? `Customer ${id}`;
}

/** One line per target from the ad hoc modal's result, or null when nothing went out */
function adhocResultLines(result: IAdhocActionResponse | IAdhocActionResponseNew, suggestion: IAssistantSuggestionItem): string[] | null {
  if (`createdActions` in result) {
    if (!result.createdActions.length) return null;

    return [
      ...result.createdActions.map(action => `${action.invoiceNumber || action.contactName || `Action`}: queued`),
      ...result.failed.map(failed => `${failed.identifier}: not sent (${failed.reason})`),
    ];
  }

  if (result.action?.status !== ActionStatus.COMPLETE) return null;

  return [`${targetName(suggestion, suggestion.contactIds[0] ?? suggestion.invoiceIds[0])}: sent`];
}

/**
 * AI review of the receivables: click to generate, then run, dismiss or discuss each suggestion.
 * Gated on the AI_SUGGESTIONS org flag in WidgetsRoot.
 */
export function AiSuggestions() {
  const dispatch = useDispatch();
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const organisationId = currentOrg?.id as string;
  const adminOverride = !!user?.adminOverride;
  // Admin override: read-only test runs are hidden from the org's users and cannot send anything
  const [readOnly, setReadOnly] = useState(true);
  const effectiveReadOnly = adminOverride && readOnly;
  const missingConsent = !!currentOrg && currentOrg.aiConsentGiven !== true && !adminOverride;
  const canEdit = !!currentOrg?.users?.some(orgUser => orgUser.id === user?.id && orgUser.permissionSettings === PermissionLevel.EDITOR);
  const assistantEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_ASSISTANT) || adminOverride;
  const queryArgs = { organisationId, readOnly: effectiveReadOnly };

  const [polling, setPolling] = useState(false);
  const { data, isLoading } = useGetAssistantSuggestionsQuery(queryArgs, {
    skip: !organisationId || missingConsent,
    // The run happens on a worker; poll until the row is filled in
    pollingInterval: polling ? 1500 : 0,
  });
  const [generate, generateState] = useGenerateAssistantSuggestionsMutation();
  const [apply, applyState] = useApplyAssistantSuggestionMutation();
  const [dismiss, dismissState] = useDismissAssistantSuggestionMutation();
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<{ index: number; suggestion: IAssistantSuggestionItem } | null>(null);
  const [escalating, setEscalating] = useState<{ runId: number; index: number; suggestion: IAssistantSuggestionItem } | null>(null);
  const [complete] = useCompleteAssistantSuggestionMutation();
  const { openBulkReminders, openBulkStatements } = useAdhocActionModal();

  const run = data?.run ?? null;
  const running = run?.status === `running`;
  const busy = generateState.isLoading || applyState.isLoading || dismissState.isLoading;

  useEffect(() => {
    setPolling(running);
  }, [running, run?.id]);

  async function onGenerate() {
    try {
      await generate(queryArgs).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not start the review.`));
    }
  }

  async function onApply(index: number, suggestion: IAssistantSuggestionItem) {
    if (!run) return;

    setConfirming(null);
    setBusyIndex(index);

    try {
      await apply({ ...queryArgs, runId: run.id, index }).unwrap();
      toast.success(`Done: ${suggestion.title}`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not run that suggestion.`));
    }
    finally {
      setBusyIndex(null);
    }
  }

  /** After the app's own dialog sent it, record the outcome on the suggestion */
  async function markDone(runId: number, index: number, suggestion: IAssistantSuggestionItem, results: string[]) {
    try {
      await complete({ ...queryArgs, runId, index, results }).unwrap();
      toast.success(`Done: ${suggestion.title}`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `It went out, but the suggestion could not be marked done.`));
    }
  }

  /** Reminders, statements and escalations open the app's own dialogs; SMS and policy changes confirm here */
  function onDoIt(index: number, suggestion: IAssistantSuggestionItem) {
    if (!run) return;

    const runId = run.id;
    const onSuccess = (result: IAdhocActionResponse | IAdhocActionResponseNew) => {
      const lines = adhocResultLines(result, suggestion);

      if (lines) markDone(runId, index, suggestion, lines);
    };

    if (suggestion.actionType === `send_invoice_reminders`) {
      openBulkReminders(suggestion.invoiceIds, { checkChaseable: true, onSuccess });
    }
    else if (suggestion.actionType === `send_statements`) {
      openBulkStatements(suggestion.contactIds, { onSuccess });
    }
    else if (suggestion.actionType === `raise_escalations`) {
      setEscalating({ runId, index, suggestion });
    }
    else {
      setConfirming({ index, suggestion });
    }
  }

  async function onDismiss(index: number) {
    if (!run) return;

    setBusyIndex(index);

    try {
      await dismiss({ ...queryArgs, runId: run.id, index }).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not dismiss that suggestion.`));
    }
    finally {
      setBusyIndex(null);
    }
  }

  const suggestions = useMemo(() => run?.suggestions ?? [], [run]);
  const pending = suggestions.filter(suggestion => suggestion.status === `pending`).length;
  const generatedAgo = run?.finishedAt ? DateTime.fromISO(run.finishedAt).toRelative() : null;
  const [selectedIndex, setSelectedIndex] = useState(0);

  // A new run starts on its first suggestion
  useEffect(() => {
    setSelectedIndex(0);
  }, [run?.id]);

  const selected = suggestions[selectedIndex] ?? suggestions[0];
  const nextPending = suggestions.findIndex((suggestion, index) => index !== selectedIndex && suggestion.status === `pending`);
  const reviewedLine = run?.status === `done`
    ? `Reviewed ${generatedAgo}${run.costUsd !== undefined ? ` · $${run.costUsd.toFixed(4)}` : ``}`
    : `Let the AI review your receivables and suggest what to do next`;

  return (
    <div className={ `w-full h-full rounded-xl bg-gray-50 ring-1 ring-black/5 p-4 sm:p-7` }>
      <div className={ `flex flex-col sm:flex-row sm:items-start justify-between gap-4` }>
        <div className={ `flex flex-col gap-1 max-w-3xl` }>
          <span className={ `text-lg font-semibold text-gray-900` }>{ `AI Suggestions` }</span>
          <When condition={ run?.status === `done` && !!run?.summary }>
            <div className={ `text-sm leading-relaxed text-gray-700` }>
              <AssistantMarkdown content={ run?.summary ?? `` } />
            </div>
          </When>
          <span className={ `text-xs text-gray-500` }>{ reviewedLine }</span>
        </div>
        <div className={ `flex items-center gap-3 shrink-0` }>
          <When condition={ adminOverride }>
            <Toggle
              label={ `Read-only test` }
              checked={ readOnly }
              onChange={ ({ value }) => setReadOnly(value) }
              disabled={ running || busy }
            />
          </When>
          <button
            type={ `button` }
            className={ PRIMARY_BUTTON }
            disabled={ missingConsent || running || busy }
            onClick={ onGenerate }
          >
            { running || generateState.isLoading ? `Reviewing...` : run ? `Generate again` : `Generate suggestions` }
            <SparklesIcon className={ `h-4 w-4` } />
          </button>
        </div>
      </div>

      <When condition={ effectiveReadOnly }>
        <p className={ `mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800` }>
          { `Read-only test run. Hidden from this organisation's users and nothing can be sent from it.` }
        </p>
      </When>

      {
        missingConsent
          ? <p className={ `mt-5 text-sm text-gray-500` }>{ `Your organisation needs to consent to AI data use first. Open the assistant to consent.` }</p>
          : isLoading
            ? <div className={ `mt-5` }>
              <LoadingElementPlaceholder
                border={ false }
                rows={ 4 }
              />
            </div>
            : !run
              ? <div className={ `mt-5 rounded-xl border border-gray-200 bg-white px-6 py-10 flex flex-col items-center text-center gap-2` }>
                <SparklesIcon className={ `w-10 h-10 text-gray-300` } />
                <p className={ `text-sm text-gray-500 max-w-md` }>
                  { `The AI looks at balances, what is scheduled, what ran and who is behind, then suggests actions and setup changes.` }
                </p>
              </div>
              : run.status === `failed`
                ? <p className={ `mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700` }>
                  { run.error ?? `The review failed. Please try again.` }
                </p>
                : <div className={ `mt-5 grid grid-cols-1 lg:grid-cols-[330px_minmax(0,1fr)] rounded-xl border border-gray-200 bg-white overflow-hidden lg:min-h-[440px]` }>
                  <Queue
                    running={ running }
                    suggestions={ suggestions }
                    pending={ pending }
                    selectedIndex={ selectedIndex }
                    onSelect={ setSelectedIndex }
                  />
                  <div className={ `flex flex-col` }>
                    {
                      running || !selected
                        ? <ReviewProgress
                          events={ run.events }
                          running={ running }
                        />
                        : <Focus
                          suggestion={ selected }
                          busy={ busy }
                          itemBusy={ busyIndex === selectedIndex }
                          canRun={ canEdit && !effectiveReadOnly }
                          assistantEnabled={ assistantEnabled }
                          hasNext={ nextPending !== -1 }
                          onDoIt={ () => onDoIt(selectedIndex, selected) }
                          onDismiss={ () => onDismiss(selectedIndex) }
                          onDiscuss={ () => dispatch(openAssistant(selected.assistantPrompt)) }
                          onNext={ () => setSelectedIndex(nextPending) }
                        />
                    }
                  </div>
                </div>
      }

      <When condition={ run?.status === `done` && run.events.length > 0 }>
        <details className={ `mt-3` }>
          <summary className={ `cursor-pointer text-xs text-gray-500` }>{ `What the AI looked at (${toSteps(run?.events ?? []).length} steps)` }</summary>
          <div className={ `mt-2` }>
            <StepList
              events={ run?.events ?? [] }
              running={ false }
            />
          </div>
        </details>
      </When>
      <BuilderConfirmModal
        open={ !!confirming }
        title={ confirming ? `${doItLabel(confirming.suggestion)}?` : `` }
        message={ confirming?.suggestion.actionType ? actionOutcome[confirming.suggestion.actionType] : `This changes your policies.` }
        confirmLabel={ confirming ? doItLabel(confirming.suggestion) ?? `Do it` : `` }
        busy={ busy }
        onConfirm={ () => confirming && onApply(confirming.index, confirming.suggestion) }
        onCancel={ () => setConfirming(null) }
      />
      <When condition={ !!escalating }>
        { () => escalating && (
          <RaiseEscalationModal
            open
            setOpen={ isOpen => !isOpen && setEscalating(null) }
            contacts={ escalating.suggestion.contactIds.map(id => ({ id, name: targetName(escalating.suggestion, id) })) }
            defaultType={ escalating.suggestion.escalationType ?? undefined }
            defaultSteps={ escalating.suggestion.detail }
            defaultAssignedUserId={ RECORD_ASSIGNEE }
            onRaised={ results => markDone(escalating.runId, escalating.index, escalating.suggestion, results) }
          />
        ) }
      </When>
    </div>
  );
}

function Queue({ running, suggestions, pending, selectedIndex, onSelect }: {
  running: boolean;
  suggestions: IAssistantSuggestionItem[];
  pending: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div className={ `flex flex-col bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-200` }>
      <div className={ `px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100` }>
        { running ? `Finding suggestions...` : suggestions.length ? `${pending} to review` : `Nothing to suggest right now` }
      </div>
      {
        running
          ? <div className={ `p-4 flex flex-col gap-4` }>
            { [85, 70, 78].map(width => (
              <div
                key={ width }
                className={ `flex flex-col gap-1.5` }
              >
                <span className={ `h-2 w-[30%] rounded bg-gray-100` } />
                <span
                  className={ `h-2.5 rounded bg-gray-100` }
                  style={ { width: `${width}%` } }
                />
              </div>
            )) }
          </div>
          : suggestions.map((suggestion, index) => {
            const priority = priorityStyle[suggestion.priority];
            const handled = suggestion.status !== `pending`;

            return (
              <button
                type={ `button` }
                key={ index }
                onClick={ () => onSelect(index) }
                className={ classNames(
                  `px-4 py-3 flex flex-col gap-1 text-left border-b border-gray-100 cursor-pointer hover:bg-paidniceGreen-50`,
                  index === selectedIndex && `bg-paidniceGreen-50`,
                  handled && suggestion.status !== `processing` && `opacity-55`,
                ) }
              >
                <span className={ `flex justify-between items-center text-[11px] font-semibold tracking-wide` }>
                  <span className={ priority.text }>{ priority.label.toUpperCase() }</span>
                  <HandledMark status={ suggestion.status } />
                </span>
                <span className={ `text-[13px] font-semibold leading-snug text-gray-900` }>{ suggestion.title }</span>
                <span className={ `text-xs text-gray-500` }>{ impactLine(suggestion) }</span>
              </button>
            );
          })
      }
    </div>
  );
}

function HandledMark({ status }: { status: IAssistantSuggestionItem[`status`] }) {
  if (status === `done`) return <span className={ `text-green-600` }>{ `✓` }</span>;

  if (status === `failed`) return <span className={ `text-red-600` }>{ `!` }</span>;

  if (status === `dismissed`) return <span className={ `text-gray-400` }>{ `–` }</span>;

  if (status === `processing`) return <span className={ `text-gray-400` }>{ `•••` }</span>;

  return null;
}

/** Tool steps as ticked lines, with the one in flight (or the write-up) last and bold */
function StepList({ events, running }: { events: IAssistantEvent[]; running: boolean }) {
  const steps = toSteps(events);
  const inFlight = steps.find(step => !step.finished);
  const done = steps.filter(step => step.finished);
  const current = inFlight?.label ?? (done.length ? `Writing up the suggestions` : `Starting the review`);

  return (
    <div className={ `flex flex-col gap-2.5` }>
      { done.map((step, index) => (
        <div
          key={ `${step.key}-${index}` }
          className={ classNames(`flex gap-2.5 text-[13px]`, step.warning ? `text-amber-700` : `text-gray-600`) }
        >
          <span className={ step.warning ? `text-amber-500` : `text-paidniceGreen-600` }>{ step.warning ? `!` : `✓` }</span>
          { step.label }
        </div>
      )) }
      <When condition={ running }>
        <div className={ `flex gap-2.5 text-[13px] font-semibold text-gray-900` }>
          <span className={ `text-gray-400 animate-pulse` }>{ `•••` }</span>
          { current }
        </div>
      </When>
    </div>
  );
}

function ReviewProgress({ events, running }: { events: IAssistantEvent[]; running: boolean }) {
  return (
    <div className={ `p-6 flex flex-col gap-2.5` }>
      <span className={ `text-[15px] font-semibold text-gray-900` }>{ running ? `Reviewing your account` : `Nothing to suggest right now` }</span>
      <StepList
        events={ events }
        running={ running }
      />
    </div>
  );
}

function Focus({ suggestion, busy, itemBusy, canRun, assistantEnabled, hasNext, onDoIt, onDismiss, onDiscuss, onNext }: {
  suggestion: IAssistantSuggestionItem;
  busy: boolean;
  itemBusy: boolean;
  canRun: boolean;
  assistantEnabled: boolean;
  hasNext: boolean;
  onDoIt: () => void;
  onDismiss: () => void;
  onDiscuss: () => void;
  onNext: () => void;
}) {
  const priority = priorityStyle[suggestion.priority];
  const label = doItLabel(suggestion);
  const targets = suggestion.targets ?? [];
  const itemsLabel = suggestion.proposal
    ? `Changes`
    : suggestion.actionType === `send_invoice_reminders` ? `Invoices` : `Customers`;
  const outcome = suggestion.proposal
    ? `Applies this change to your policies.`
    : suggestion.actionType ? actionOutcome[suggestion.actionType] : `Advice only. There is nothing to run.`;

  return (
    <div className={ `px-5 sm:px-7 py-6 flex flex-col gap-5 flex-1` }>
      <div className={ `flex flex-col gap-2` }>
        <span className={ classNames(`self-start text-xs font-semibold px-2 py-0.5 rounded-full`, priority.pill) }>{ `${priority.label} priority` }</span>
        <span className={ `text-xl font-semibold leading-snug text-gray-900` }>{ suggestion.title }</span>
        <div className={ `text-sm leading-relaxed text-gray-600` }>
          <AssistantMarkdown content={ suggestion.detail } />
        </div>
      </div>

      <When condition={ !!suggestion.proposal || targets.length > 0 }>
        <div className={ `flex flex-col gap-1.5` }>
          <span className={ `text-xs font-semibold text-gray-500` }>{ itemsLabel }</span>
          <div className={ `rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100` }>
            {
              suggestion.proposal
                ? suggestion.proposal.ops.map((op, index) => (
                  <div
                    key={ index }
                    className={ `px-3.5 py-2.5 text-[13px] text-gray-700` }
                  >
                    { op.summary }
                  </div>
                ))
                : targets.map(target => (
                  <div
                    key={ target.id }
                    className={ `grid grid-cols-[minmax(0,1fr)_auto_110px] gap-4 px-3.5 py-2.5 text-[13px]` }
                  >
                    <span className={ `font-semibold text-gray-900 truncate` }>{ target.name }</span>
                    <span className={ `text-gray-500` }>{ target.meta }</span>
                    <span className={ `text-right font-medium tabular-nums` }>
                      { target.amountCents !== null && target.currency ? formatCents(target.amountCents, target.currency) : `` }
                    </span>
                  </div>
                ))
            }
          </div>
        </div>
      </When>

      <When condition={ !!suggestion.smsBody }>
        <div className={ `flex flex-col gap-1.5` }>
          <span className={ `text-xs font-semibold text-gray-500` }>{ `SMS preview` }</span>
          <div className={ `self-start max-w-md rounded-[14px] rounded-bl-[4px] bg-paidniceGreen-50 px-3.5 py-2.5 text-[13px] text-paidniceGreen-800` }>
            { suggestion.smsBody }
          </div>
        </div>
      </When>

      <When condition={ !!suggestion.results?.length }>
        <div className={ `flex flex-col gap-1.5` }>
          <span className={ `text-xs font-semibold text-gray-500` }>{ `Results` }</span>
          <ul className={ `rounded-lg border border-gray-200 divide-y divide-gray-100 text-[13px] text-gray-700` }>
            { suggestion.results?.map((result, index) => <li
              key={ index }
              className={ `px-3.5 py-2` }
            >{ result }</li>) }
          </ul>
        </div>
      </When>

      <div className={ `flex-1` } />

      {
        suggestion.status === `pending`
          ? <div className={ `flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4` }>
            <span className={ `flex-1 min-w-[200px] text-[13px] text-gray-600` }>{ outcome }</span>
            <When condition={ assistantEnabled && !!suggestion.assistantPrompt }>
              <button
                type={ `button` }
                className={ SECONDARY_BUTTON }
                disabled={ busy }
                onClick={ onDiscuss }
              >
                { `Discuss` }
              </button>
            </When>
            <button
              type={ `button` }
              className={ SECONDARY_BUTTON }
              disabled={ busy }
              onClick={ onDismiss }
            >
              { `Dismiss` }
            </button>
            <When condition={ !!label && canRun }>
              <button
                type={ `button` }
                className={ PRIMARY_BUTTON }
                disabled={ busy }
                onClick={ onDoIt }
              >
                { itemBusy ? `Working...` : label }
              </button>
            </When>
          </div>
          : <div className={ `flex flex-wrap items-center gap-2.5 border-t border-gray-100 pt-4 text-[13px] text-gray-700` }>
            <HandledMark status={ suggestion.status } />
            <span className={ classNames(`flex-1`, suggestion.status === `failed` && `text-red-700`) }>
              {
                suggestion.status === `done`
                  ? suggestion.proposal ? `Applied` : `Done`
                  : suggestion.status === `processing`
                    ? `Running...`
                    : suggestion.status === `failed`
                      ? suggestion.error ?? `This did not finish`
                      : `Dismissed`
              }
            </span>
            <When condition={ hasNext }>
              <button
                type={ `button` }
                className={ `${BUTTON} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50` }
                onClick={ onNext }
              >
                { `Next suggestion →` }
              </button>
            </When>
          </div>
      }
    </div>
  );
}
